import { useEffect, useMemo, useState } from 'react'

export type DateTimeValue = {
  date: string // YYYY-MM-DD
  time: string // HH:mm
  isNow?: boolean
}

type DateTimePickerProps = {
  value: DateTimeValue | null
  minDate?: string
  minDateTime?: DateTimeValue | null
  allowNow?: boolean
  onConfirm: (value: DateTimeValue) => void
  onCancel?: () => void
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function toDateKey(year: number, month: number, day: number) {
  return `${year}-${pad(month + 1)}-${pad(day)}`
}

function parseDateKey(key: string) {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function todayKey() {
  const now = new Date()
  return toDateKey(now.getFullYear(), now.getMonth(), now.getDate())
}

function currentTime() {
  const now = new Date()
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`
}

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: Array<{ day: number; key: string } | null> = []

  for (let i = 0; i < firstDay; i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ day, key: toDateKey(year, month, day) })
  }
  return cells
}

const HOURS = Array.from({ length: 24 }, (_, i) => pad(i))
const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5))

export default function DateTimePicker({
  value,
  minDate,
  minDateTime,
  allowNow = false,
  onConfirm,
  onCancel,
}: DateTimePickerProps) {
  const initialDate = value?.date || todayKey()
  const initial = parseDateKey(initialDate)

  const [viewYear, setViewYear] = useState(initial.getFullYear())
  const [viewMonth, setViewMonth] = useState(initial.getMonth())
  const [selectedDate, setSelectedDate] = useState(value?.date || '')
  const [selectedHour, setSelectedHour] = useState(
    value?.time ? value.time.slice(0, 2) : '10',
  )
  const [selectedMinute, setSelectedMinute] = useState(
    value?.time ? value.time.slice(3, 5) : '00',
  )
  const [useNow, setUseNow] = useState(Boolean(value?.isNow))

  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  )

  const effectiveMinDate = minDate || minDateTime?.date || todayKey()

  useEffect(() => {
    if (!useNow) return
    const time = currentTime()
    setSelectedDate(todayKey())
    setSelectedHour(time.slice(0, 2))
    setSelectedMinute(pad(Math.floor(Number(time.slice(3, 5)) / 5) * 5))
  }, [useNow])

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear((y) => y - 1)
      return
    }
    setViewMonth((m) => m - 1)
  }

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear((y) => y + 1)
      return
    }
    setViewMonth((m) => m + 1)
  }

  const isDisabledDate = (key: string) => key < effectiveMinDate

  const isTimeDisabled = (hour: string, minute: string) => {
    if (!minDateTime || !selectedDate) return false
    if (selectedDate > minDateTime.date) return false
    if (selectedDate < minDateTime.date) return true
    return `${hour}:${minute}` <= minDateTime.time
  }

  const canConfirm =
    Boolean(selectedDate) &&
    !isDisabledDate(selectedDate) &&
    !isTimeDisabled(selectedHour, selectedMinute)

  const handleConfirm = () => {
    if (!canConfirm) return

    if (useNow) {
      onConfirm({
        date: todayKey(),
        time: currentTime(),
        isNow: true,
      })
      return
    }

    onConfirm({
      date: selectedDate,
      time: `${selectedHour}:${selectedMinute}`,
      isNow: false,
    })
  }

  return (
    <div
      className="absolute left-0 top-[calc(100%+12px)] z-[120] w-[min(520px,calc(100vw-32px))] overflow-hidden rounded-[16px] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.22)]"
      role="dialog"
      aria-label="Select date and time"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Calendar */}
        <div className="min-w-0 flex-1 px-[16px] py-[14px] sm:border-r sm:border-[#EDEDED]">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goPrevMonth}
              className="flex h-[32px] w-[32px] items-center justify-center rounded-full text-black transition-colors hover:bg-[#F3F3F3]"
              aria-label="Previous month"
            >
              ‹
            </button>
            <p className="font-poppins text-[15px] font-semibold text-black">
              {MONTHS[viewMonth]} {viewYear}
            </p>
            <button
              type="button"
              onClick={goNextMonth}
              className="flex h-[32px] w-[32px] items-center justify-center rounded-full text-black transition-colors hover:bg-[#F3F3F3]"
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <div className="mt-[12px] grid grid-cols-7 gap-[4px]">
            {WEEKDAYS.map((day) => (
              <span
                key={day}
                className="text-center font-poppins text-[11px] font-medium text-[#9A9A9A]"
              >
                {day}
              </span>
            ))}
            {cells.map((cell, index) => {
              if (!cell) {
                return <span key={`empty-${index}`} />
              }

              const disabled = isDisabledDate(cell.key)
              const selected = selectedDate === cell.key && !useNow

              return (
                <button
                  key={cell.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setUseNow(false)
                    setSelectedDate(cell.key)
                  }}
                  className={`flex h-[34px] items-center justify-center rounded-full font-poppins text-[13px] font-medium transition-colors ${
                    selected
                      ? 'bg-brand-orange text-white'
                      : disabled
                        ? 'cursor-not-allowed text-[#C8C8C8]'
                        : 'text-black hover:bg-[#FFF1E8]'
                  }`}
                >
                  {cell.day}
                </button>
              )
            })}
          </div>
        </div>

        {/* Time — right side of calendar */}
        <div className="flex w-full flex-col border-t border-[#EDEDED] px-[14px] py-[14px] sm:w-[168px] sm:border-t-0">
          <div className="mb-[10px] flex items-center justify-between gap-[8px]">
            <p className="font-poppins text-[13px] font-semibold tracking-[-0.02em] text-black">
              Time
            </p>
            {allowNow && (
              <button
                type="button"
                onClick={() => setUseNow(true)}
                className={`rounded-full px-[10px] py-[4px] font-poppins text-[12px] font-medium transition-colors ${
                  useNow
                    ? 'bg-brand-orange text-white'
                    : 'bg-[#F3F3F3] text-black hover:bg-[#EAEAEA]'
                }`}
              >
                NOW
              </button>
            )}
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-2 gap-[8px]">
            <div className="flex min-h-0 flex-col">
              <p className="mb-[6px] text-center font-poppins text-[11px] font-medium text-[#8F8F8F]">
                Hour
              </p>
              <div className="from-dropdown-scrollbar max-h-[220px] flex-1 overflow-y-auto rounded-[12px] border border-[#E8E8E8]">
                {HOURS.map((hour) => {
                  const disabled = useNow || isTimeDisabled(hour, selectedMinute)
                  const selected = !useNow && selectedHour === hour
                  return (
                    <button
                      key={hour}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setUseNow(false)
                        setSelectedHour(hour)
                      }}
                      className={`flex h-[34px] w-full items-center justify-center font-poppins text-[13px] font-medium transition-colors ${
                        selected
                          ? 'bg-brand-orange text-white'
                          : disabled
                            ? 'cursor-not-allowed text-[#C8C8C8]'
                            : 'text-black hover:bg-[#FFF1E8]'
                      }`}
                    >
                      {hour}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="flex min-h-0 flex-col">
              <p className="mb-[6px] text-center font-poppins text-[11px] font-medium text-[#8F8F8F]">
                Min
              </p>
              <div className="from-dropdown-scrollbar max-h-[220px] flex-1 overflow-y-auto rounded-[12px] border border-[#E8E8E8]">
                {MINUTES.map((minute) => {
                  const disabled =
                    useNow || isTimeDisabled(selectedHour, minute)
                  const selected = !useNow && selectedMinute === minute
                  return (
                    <button
                      key={minute}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setUseNow(false)
                        setSelectedMinute(minute)
                      }}
                      className={`flex h-[34px] w-full items-center justify-center font-poppins text-[13px] font-medium transition-colors ${
                        selected
                          ? 'bg-brand-orange text-white'
                          : disabled
                            ? 'cursor-not-allowed text-[#C8C8C8]'
                            : 'text-black hover:bg-[#FFF1E8]'
                      }`}
                    >
                      {minute}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-[10px] border-t border-[#EDEDED] px-[16px] py-[12px]">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-[42px] flex-1 items-center justify-center rounded-[10px] bg-[#F3F3F3] font-poppins text-[14px] font-semibold text-black transition-colors hover:bg-[#EAEAEA]"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          disabled={!canConfirm}
          onClick={handleConfirm}
          className="flex h-[42px] flex-1 items-center justify-center rounded-[10px] bg-brand-orange font-poppins text-[14px] font-semibold text-white transition-colors hover:bg-[#e5631f] disabled:cursor-not-allowed disabled:bg-[#D8D8D8] disabled:text-[#8F8F8F]"
        >
          Done
        </button>
      </div>
    </div>
  )
}

export function formatDateLabel(dateStr: string) {
  if (!dateStr) return 'Select date'
  const date = parseDateKey(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`
}

export function formatTimeLabel(value: DateTimeValue | null) {
  if (!value) return 'Select time'
  if (value.isNow) return 'NOW'
  return value.time
}
